import { ConvertMMToPixel, ConvertPixelToMM } from "./convertCMToPixel";

/**
 * Niveaux de qualité photo pour l'impression grand public
 */
export enum PhotoQualityLevel {
  EXCELLENT = 'excellent',  // 🟢 > 200 DPI
  GOOD = 'good',           // 🟠 120-200 DPI  
  POOR = 'poor'            // 🔴 < 120 DPI
}

/**
 * Calcule le DPI réel d'une photo dans un élément donné
 * 
 * @param imageWidth Largeur originale de l'image en pixels
 * @param imageHeight Hauteur originale de l'image en pixels
 * @param elementWidth Largeur de l'élément en mm (workspace units)
 * @param elementHeight Hauteur de l'élément en mm (workspace units)
 * @param zoom Zoom appliqué en pourcentage (100 = taille normale)
 * @returns DPI effectif de l'image
 */
export const calculateEffectiveDPI = (
  imageWidth: number,
  imageHeight: number,
  elementWidth: number,
  elementHeight: number,
  zoom: number
): { dpiWidth: number; dpiHeight: number; effectiveDPI: number } => {
  // Éviter la division par zéro
  if (imageWidth <= 0 || imageHeight <= 0 || elementWidth <= 0 || elementHeight <= 0 || zoom <= 0) {
    return { dpiWidth: 0, dpiHeight: 0, effectiveDPI: 0 };
  }

  // CORRECTION : Calculer les pixels effectifs selon le zoom
  // Plus le zoom est élevé, moins on utilise de pixels de l'image originale
  const effectivePixelsWidth = imageWidth / (zoom / 100);
  const effectivePixelsHeight = imageHeight / (zoom / 100);

  // Les elementWidth/Height sont en unités workspace, il faut les convertir en mm
  const finalWidthMM = ConvertPixelToMM(elementWidth);
  const finalHeightMM = ConvertPixelToMM(elementHeight);

  // Convertir en inches pour le calcul DPI (1 inch = 25.4 mm)
  const finalWidthInches = finalWidthMM / 25.4;
  const finalHeightInches = finalHeightMM / 25.4;

  // Calculer le DPI pour chaque dimension avec les pixels effectifs
  const dpiWidth = effectivePixelsWidth / finalWidthInches;
  const dpiHeight = effectivePixelsHeight / finalHeightInches;

  // Le DPI effectif est le plus petit (contrainte la plus forte)
  const effectiveDPI = Math.min(dpiWidth, dpiHeight);

  return { dpiWidth, dpiHeight, effectiveDPI };
};

/**
 * Détermine le niveau de qualité d'une photo selon des seuils adaptés au grand public
 * 
 * @param imageWidth Largeur originale de l'image en pixels
 * @param imageHeight Hauteur originale de l'image en pixels
 * @param elementWidth Largeur de l'élément en mm (workspace units)
 * @param elementHeight Hauteur de l'élément en mm (workspace units)
 * @param zoom Zoom appliqué en pourcentage (100 = taille normale)
 * @returns Niveau de qualité avec DPI calculé
 */
export const calculatePhotoQuality = (
  imageWidth: number,
  imageHeight: number,
  elementWidth: number,
  elementHeight: number,
  zoom: number
): { level: PhotoQualityLevel; dpi: number; dpiWidth: number; dpiHeight: number } => {
  // Debug: Log des valeurs pour identifier le problème
  const finalWidthMM = ConvertPixelToMM(elementWidth);
  const finalHeightMM = ConvertPixelToMM(elementHeight);
  console.log('🔍 DEBUG calculatePhotoQuality:', {
    imageWidth,
    imageHeight, 
    elementWidth,
    elementHeight,
    zoom,
    'elementWidth(workspace)': elementWidth,
    'elementHeight(workspace)': elementHeight,
    'finalWidthMM': finalWidthMM,
    'finalHeightMM': finalHeightMM,
    'finalWidth(inches)': finalWidthMM / 25.4,
    'finalHeight(inches)': finalHeightMM / 25.4,
    'corrected_dpiWidth': imageWidth / (finalWidthMM / 25.4),
    'corrected_dpiHeight': imageHeight / (finalHeightMM / 25.4)
  });

  const { dpiWidth, dpiHeight, effectiveDPI } = calculateEffectiveDPI(
    imageWidth, 
    imageHeight, 
    elementWidth, 
    elementHeight, 
    zoom
  );

  // Seuils adaptés au grand public (particuliers)
  let level: PhotoQualityLevel;
  if (effectiveDPI >= 200) {
    level = PhotoQualityLevel.EXCELLENT;  // 🟢 Excellente qualité
  } else if (effectiveDPI >= 120) {
    level = PhotoQualityLevel.GOOD;       // 🟠 Qualité convenable
  } else {
    level = PhotoQualityLevel.POOR;       // 🔴 Qualité limite
  }

  return {
    level,
    dpi: Math.round(effectiveDPI),
    dpiWidth: Math.round(dpiWidth),
    dpiHeight: Math.round(dpiHeight)
  };
};

/**
 * Fonction utilitaire pour la compatibilité avec l'ancien système boolean
 * @deprecated Utiliser calculatePhotoQuality() à la place
 */
export const isGoodQuality = (
  imageWidth: number,
  imageHeight: number,
  elementWidth: number,
  elementHeight: number,
  zoom: number
): boolean => {
  const { level } = calculatePhotoQuality(imageWidth, imageHeight, elementWidth, elementHeight, zoom);
  return level !== PhotoQualityLevel.POOR;
};